import { MutableRefObject } from 'react'
import cn from 'clsx'
import loadable from '@loadable/component'

// Components
import { Labelled } from '@tracklib/kit'

import styles from './phoneinput.css'

// Loadable
const PhoneInputLoadable = loadable.lib(() => import('react-phone-number-input'))

type PhoneInputProps = {
  // ...input
  name: string
  value: string | number
  onChange: (input: string) => unknown
  onBlur?: (e: Event) => string
  // ...meta
  touched?: boolean
  warning?: string
  error?: string
  // ...rest
  label?: string
  description?: string
  placeholder?: string
  disabled?: boolean
  optional?: string
  className?: string
  errorClassName?: string
  hidden?: boolean
  id?: string | number
  help?: string
  autoComplete?: boolean
  autoFocus?: boolean
  uppercase?: boolean
  onChangeInterceptor?: (e: Event) => Event
  onBlurInterceptor?: (e: Event) => Event
  inputRef?: MutableRefObject<HTMLInputElement | null>
  onClick?: () => void
  datatesting?: string
  defaultCountry?: string
  international?: boolean
}

const PhoneInputComponent = ({
  name,
  value,
  onChange,
  onBlur,
  touched = false,
  error = '',
  warning = '',
  label,
  description,
  placeholder,
  disabled,
  optional,
  className,
  errorClassName,
  hidden,
  id,
  help,
  autoComplete,
  autoFocus,
  uppercase,
  onChangeInterceptor,
  onBlurInterceptor,
  inputRef,
  onClick,
  datatesting,
  defaultCountry,
  international = true,
}: PhoneInputProps): JSX.Element => {
  const inputId = id || name

  return (
    <Labelled
      className={className}
      id={inputId}
      optional={optional}
      label={label}
      help={help}
      description={description}
      touched={touched}
      error={error}
      warning={warning}
      hidden={hidden}
      errorClassName={errorClassName}
    >
      <PhoneInputLoadable>
        {({ default: PhoneInput }) => (
          <PhoneInput
            placeholder={placeholder}
            value={value}
            onChange={e => onChange(onChangeInterceptor ? onChangeInterceptor(e) : e)}
            onBlur={
              onBlurInterceptor
                ? e => {
                    onChange(onBlurInterceptor(e))
                    onBlur(e)
                  }
                : e => onBlur && onBlur(e)
            }
            onClick={onClick}
            ref={inputRef}
            numberInputProps={{
              className: cn(
                styles.input,
                touched && error && styles.error,
                touched && warning && styles.warning,
                uppercase && styles.uppercase,
              ),
            }}
            name={name}
            id={inputId}
            disabled={disabled}
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            datatesting={datatesting}
            defaultCountry={defaultCountry}
            international={international}
          />
        )}
      </PhoneInputLoadable>
    </Labelled>
  )
}

export default PhoneInputComponent
